import type { ReactNode } from 'react'
import { LoadFailed } from './LoadFailed.js'

/**
 * The three states every loaded screen has, in one place.
 *
 * Loading, failed, ready — and the dispatch between them was already written
 * twice before the third screen existed. Each copy is a chance for one screen
 * to forget the retry, or to render a ready view against a state that is not
 * ready yet.
 *
 * The payload arrives through a render function rather than through context or
 * a prop, so the ready branch is the only place it exists and TypeScript can
 * prove it is present there. A `state.value!` inside the page would be the same
 * arrangement with the proof removed.
 */

export type AsyncState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'failed'; readonly detail: string }
  | { readonly status: 'ready'; readonly value: T }

export function Async<T>({
  state,
  skeleton,
  onRetry,
  children,
}: {
  readonly state: AsyncState<T>
  readonly skeleton: ReactNode
  readonly onRetry: () => void
  readonly children: (value: T) => ReactNode
}) {
  if (state.status === 'loading') return <>{skeleton}</>
  if (state.status === 'failed') return <LoadFailed detail={state.detail} onRetry={onRetry} />
  return <>{children(state.value)}</>
}
